import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

// Drop the leading hero block (cover image + duplicate title) that
// most CMS-rendered article HTML opens with. We already render
// article.image_url and article.title in the page header, so leaving
// these in the body is visual duplication.
//
// Trim rules:
//   - A leading markdown image (`![...](url)`), regardless of whether
//     the url matches the cover or not — the body's first image is
//     almost always the same hero.
//   - A leading H1/H2 whose normalised text starts with the article
//     title (case-insensitive, punctuation-tolerant). We compare on
//     first ~80 chars so a slightly-rewritten heading still matches.
function trimDuplicateHero(md, articleTitle) {
  if (!md) return md
  let out = md.replace(/^\s*/, '')
  // Strip a leading markdown image (image-only paragraph at the top).
  out = out.replace(/^!\[[^\]]*\]\([^)]+\)[ \t]*\n+/, '')
  out = out.replace(/^\s*/, '')
  if (articleTitle) {
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9؀-ۿ]+/gu, '').slice(0, 80)
    const titleKey = norm(articleTitle)
    if (titleKey) {
      out = out.replace(/^#{1,3}[ \t]+(.+?)[ \t]*\n+/, (match, heading) => {
        return norm(heading) === titleKey || norm(heading).startsWith(titleKey)
          ? ''
          : match
      })
    }
  }
  return out.replace(/^\s*/, '')
}

// On-demand full-article fetcher. Calls the fetch-article-content
// edge function, which proxies to Jina Reader and caches the
// extracted markdown back into news_articles.full_content. Same
// envelope shape as useAiCall — { run, content, loading, error,
// debug } — so the detail page can render loading + error states
// uniformly.
//
// Auto-fires on `article` change when the article doesn't already
// have full_content cached, so the user sees content streaming in
// without an extra tap.
export function useArticleContent(article) {
  const articleTitle = article?.title || null
  const [content, setContent] = useState(article?.full_content
    ? trimDuplicateHero(article.full_content, articleTitle)
    : null
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const lastFiredFor = useRef(null)

  const articleId = article?.id || null
  const cachedContent = article?.full_content || null

  // When the article changes, reset to whatever the row already has
  // (also runs through trimDuplicateHero so the cached body never
  // double-renders the cover + title).
  useEffect(() => {
    setContent(cachedContent ? trimDuplicateHero(cachedContent, articleTitle) : null)
    setError(null)
  }, [articleId, cachedContent, articleTitle])

  const run = useCallback(async ({ force = false } = {}) => {
    if (!articleId) return
    setLoading(true)
    setError(null)
    try {
      const response = await supabase.functions.invoke('fetch-article-content', {
        body: { article_id: articleId, force },
      })
      if (response.error) {
        let detail = response.error.message || 'request failed'
        const inner = response.error.context
        if (inner && typeof inner.text === 'function') {
          try {
            const raw = await inner.text()
            try {
              const parsed = JSON.parse(raw)
              if (parsed?.error) detail = parsed.error
              if (parsed?.detail) detail += ` — ${parsed.detail}`
            } catch { /* not JSON, use raw */ }
          } catch { /* ignore */ }
        }
        setError(detail)
      } else if (response.data?.error) {
        setError(response.data.error)
      } else if (response.data?.content) {
        setContent(trimDuplicateHero(response.data.content, articleTitle))
      } else {
        setError('empty content response')
      }
    } catch (e) {
      setError(e?.message || 'request failed')
    } finally {
      setLoading(false)
    }
  }, [articleId, articleTitle])

  // Auto-fire on first sight of the article when it's not cached.
  // Guard with a ref so we don't loop if the fetch fails and the
  // article object reference stays the same.
  useEffect(() => {
    if (!articleId) return
    if (cachedContent) return
    if (lastFiredFor.current === articleId) return
    lastFiredFor.current = articleId
    run()
  }, [articleId, cachedContent, run])

  return { content, loading, error, run }
}
